import CloseIcon from '@/components/Icon/CloseIcon';
import StopIcon from '@/components/Icon/StopIcon';
import IconButton from '@/components/IconButton/IconButton';
import SectionBackground from '@/components/SectionBackground/SectionBackground';
import useHaptics from '@/hooks/useHaptics';
import {
	Popover,
	PopoverButton,
	PopoverOverlay,
	PopoverPanel,
} from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ReactNode, useMemo } from 'react';

const BottomSheet = ({
	className,
	triggerButton,
	message,
	options,
}: {
	className?: string;
	triggerButton: {
		content: ReactNode;
		label: string;
		className?: string;
	};
	message?: string;
	options: BottomSheetButtonProps[];
}) => {
	const reduceMotion = useReducedMotion();
	const { withOnButtonPress } = useHaptics();

	return (
		<Popover className={className}>
			<PopoverButton
				aria-label={triggerButton.label}
				title={triggerButton.label}
				className={triggerButton.className}
				{...withOnButtonPress}>
				{triggerButton.content}
			</PopoverButton>
			<PopoverOverlay className="fixed inset-0 z-40 bg-secondary opacity-50" />
			<PopoverPanel className="fixed inset-x-0 bottom-0 z-50 w-full">
				{({ close }) => (
					<motion.nav
						className="relative mx-auto max-h-screen w-full max-w-lg overflow-y-auto rounded-t-md bg-primary py-4 text-white"
						initial={{
							y: reduceMotion ? 0 : '100%',
						}}
						animate={{
							y: 0,
						}}
						exit={{
							y: reduceMotion ? 0 : '100%',
						}}>
						<div className="mx-auto w-fit pb-2">
							<IconButton
								onClick={() => close()}
								icon={<CloseIcon />}
								title="Sluiten"
								aria-label="Sluiten"
								variant="accent"
							/>
						</div>
						{!!message && (
							<p className="text-balance px-6 pb-4 text-center font-sans text-xl">
								{message}
							</p>
						)}
						<ul
							aria-label="Opties"
							className="grid gap-4 overflow-y-auto">
							{options.map((option) => (
								<li key={option.label}>
									<BottomSheetButton
										{...option}
										onClick={() => {
											option?.onClick?.();
											close();
										}}
										onConfirmationClosed={() => close()}
									/>
								</li>
							))}
						</ul>
					</motion.nav>
				)}
			</PopoverPanel>
		</Popover>
	);
};

type BottomSheetButtonProps = {
	icon: ReactNode;
	label: string;
	confirmation?: {
		message: string;
		positiveLabel: string;
		negativeLabel: string;
	};
	isVisible?: boolean;
	onClick?: () => void;
	onConfirmationClosed?: () => void;
};

const BottomSheetButton = ({
	icon,
	label,
	confirmation,
	isVisible = true,
	onClick,
	onConfirmationClosed,
}: BottomSheetButtonProps) => {
	const { withOnButtonPress } = useHaptics();

	const buttonContent = useMemo(
		() => (
			<motion.div
				className="relative font-brand"
				whileTap={{
					scaleY: 0.9,
				}}
				tabIndex={-1}>
				<SectionBackground className="text-accent group-focus-visible:opacity-50" />
				<div className="relative flex items-center gap-4 p-4 text-start text-3xl text-accentCompat">
					{icon}
					{label}
				</div>
			</motion.div>
		),
		[icon, label],
	);

	if (!isVisible) return null;

	if (!confirmation) {
		return (
			<button
				type="button"
				className="group w-full"
				onClick={onClick}
				{...withOnButtonPress}>
				{buttonContent}
			</button>
		);
	}

	// Else return another bottom sheet button with a confirmation dialog
	return (
		<BottomSheet
			className="relative my-auto w-full"
			triggerButton={{
				content: buttonContent,
				label: label,
				className: 'group w-full',
			}}
			message={confirmation.message}
			options={[
				{
					label: confirmation.positiveLabel,
					icon,
					onClick: () => {
						onClick?.();
						onConfirmationClosed?.();
					},
				},
				{
					label: confirmation.negativeLabel,
					icon: <StopIcon />,
					onClick: onConfirmationClosed,
				},
			]}
		/>
	);
};

export default BottomSheet;
